import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { getArticle, getSettings, getImageUrl } from "@/lib/api";
import { ArrowLeft, Calendar } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import GradientBackground from "@/components/GradientBackground";
import Footer from "@/components/Footer";

export default function BlogArticle() {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getArticle(id), getSettings()]).then(([art, sett]) => {
      setArticle(art);
      setSettings(sett);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f]" data-testid="article-loading">
        <div className="w-8 h-8 border-2 border-pink-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f]" data-testid="article-not-found">
        <div className="text-center">
          <h2 className="font-['Outfit'] text-2xl font-bold text-white mb-2">Artikel nicht gefunden</h2>
          <Link to="/blog" className="text-[#ff0090] hover:text-[#ff69b4] text-sm font-medium">Zurück zum Blog</Link>
        </div>
      </div>
    );
  }

  const formattedDate = article.created_at
    ? new Date(article.created_at).toLocaleDateString('de-DE', { year: 'numeric', month: 'long', day: 'numeric' })
    : '';

  return (
    <div className="min-h-screen text-white relative overflow-x-hidden" data-testid="blog-article-page">
      {/* Animated GradFlow Background */}
      <GradientBackground />

      <nav className="sticky top-0 z-40 bg-black/70 backdrop-blur-xl border-b border-[#ff0090]/30">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center">
          <Link to="/blog" className="flex items-center gap-2 text-sm font-medium text-white/70 hover:text-white transition-colors" data-testid="back-to-blog">
            <ArrowLeft className="w-4 h-4" />
            Blog
          </Link>
        </div>
      </nav>

      <article className="relative isolate max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-12 md:py-20">
        {/* Tags */}
        {article.tags?.length > 0 && (
          <div className="flex gap-2 mb-4 flex-wrap" data-testid="article-tags">
            {article.tags.map(tag => (
              <Badge key={tag} variant="secondary" className="text-xs bg-[#ff0090]/30 text-[#ff69b4] border border-[#ff0090]/50">{tag}</Badge>
            ))}
          </div>
        )}

        <h1 className="font-['Outfit'] text-2xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-white leading-tight animate-fade-in-up" style={{ opacity: 0 }} data-testid="article-title">
          {article.title}
        </h1>

        {formattedDate && (
          <div className="flex items-center gap-2 mt-3 sm:mt-4 text-xs sm:text-sm text-white/50" data-testid="article-date">
            <Calendar className="w-4 h-4" />
            {formattedDate}
          </div>
        )}

        {/* Cover Image */}
        {article.cover_image_url && (
          <div className="mt-6 sm:mt-8 rounded-2xl overflow-hidden border-2 border-[#ff0090]/40 animate-fade-in-up animate-delay-100 shadow-lg shadow-[#ff0090]/20" style={{ opacity: 0 }}>
            <img
              src={getImageUrl(article.cover_image_url)}
              alt={article.title}
              className="w-full h-auto max-h-[300px] sm:max-h-[400px] object-cover"
              data-testid="article-cover-image"
            />
          </div>
        )}

        {/* Content */}
        <div className="mt-6 sm:mt-10 article-content text-white/70 text-base sm:text-lg leading-relaxed animate-fade-in-up animate-delay-200" style={{ opacity: 0, whiteSpace: 'pre-wrap' }} data-testid="article-content">
          {article.content}
        </div>
      </article>

      {/* Footer */}
      <Footer />
    </div>
  );
}
